import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useId, useRef, useState, useMemo } from 'react';
import __isEmpty from 'lodash/isEmpty';
import __isUndefined from 'lodash/isUndefined';
import { useUser } from 'hooks/useUser';
import { useModal } from 'hooks/3catinfo/useModal';
import { useModulAvis } from 'hooks/useModulAvis';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import { trescatinfoComponents } from 'components/3catinfo';
import Imatge from 'components/3catinfo/Imatge';
import Boto from 'components/3catinfo/Boto';
import Icona from '../../ui/Icona';
import Text from 'components/3catinfo/Text';
import styles from './onboardingBase.module.scss';

export const getOnboardingCookieName = (onboardingActiu) => {
    const audience = onboardingActiu?.usuarisLogats ? 'logat' : onboardingActiu?.usuarisNoLogats ? 'noLogat' : null;

    return `onboarding_3catinfo_${onboardingActiu?.id}_${audience}`;
};

// --- HELPER D'ADOBE TARGET PER CLICS ---
const trackTargetClick = (clickMbox, botoId) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (typeof trackEvent === 'function') {
        console.log(`🎯 Adobe Target: Trackejant clic a [${clickMbox}]`);

        trackEvent({
            mbox: clickMbox,
            params: {
                buttonId: botoId,
                elementTag: 'button'
            },
            success: () => {
                console.log('✅ Adobe Target: Clic enviat correctament');
            },
            error: (status, error) => {
                console.warn('❌ Adobe Target: Error enviant el clic', status, error);
            }
        });
    } else {
        console.warn('⚠️ Adobe Target: La funció trackEvent no està disponible');
    }
};

// --- HELPER D'ADOBE TARGET ---
const fetchTargetVariant = async (mbox, fallbackVariant = 'I') => {
    const timeout = 3000;
    const interval = 100;
    const start = Date.now();
    let target = null;

    // Esperem a que window.adobe.target estigui disponible
    while (Date.now() - start < timeout) {
        target = globalThis?.window?.adobe?.target;
        if (target) break;
        await new Promise((r) => setTimeout(r, interval));
    }

    if (!target) return fallbackVariant;

    // Demanem l'oferta
    return new Promise((resolve) => {
        try {
            if (typeof target.getOffer === 'function') {
                target.getOffer({
                    mbox,
                    success: (offer) => {
                        let variantRaw = offer?.[0]?.content?.[0]?.version;
                        const variant = typeof variantRaw === 'string' ? variantRaw.trim().toUpperCase() : fallbackVariant;
                        resolve(variant);
                    },
                    error: (error) => {
                        console.warn('Error obtenint Adobe Target Offer', error);
                        resolve(fallbackVariant);
                    }
                });
            } else {
                resolve(fallbackVariant);
            }
        } catch (error) {
            console.warn('Error obtenint Adobe Target Offer', error);
            resolve(fallbackVariant);
        }
    });
};
// ------------------------------

export default function Component(props) {
    const { onboardingActiu } = props;
    const { component } = onboardingActiu;

    // ✅ Usar base si existeix, sinó componentProps, sinó onboardingActiu
    const baseProps = onboardingActiu?.base || onboardingActiu?.componentProps || onboardingActiu;

    const { setModal, removeModal } = useModal();
    const { isModulAvisActive } = useModulAvis();
    const router = useRouter();
    const { signUp, signIn, loginState, loginStateTypes, isAuthenticated } = useUser();

    // ✅ Obtenir variants disponibles de la configuració
    const validSegments = useMemo(() => Object.keys(onboardingActiu?.variants || {}), [onboardingActiu?.variants]);
    const isValidSegment = (seg) => validSegments.length === 0 || validSegments.includes(seg);

    // ✅ Obtenir segment de Target o cookie
    const [segment, setSegment] = useState(onboardingActiu?.defaultSegment || 'I');
    const initialFetchDone = useRef(false);

    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showedToday, setShowedToday] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);
    const [showedOnboarding, setShowedOnboarding] = useState(true);
    const timeoutShowOnboarding = useRef();
    const refOnboarding = useRef();
    const isMountedRef = useRef(true);

    const usuarisLogats = onboardingActiu?.usuarisLogats || false;
    const usuarisNoLogats = onboardingActiu?.usuarisNoLogats || false;
    const ComponentOnboarding = trescatinfoComponents[component]?.dynamicComponent;
    const isTest =
        typeof window !== 'undefined' && window.Cypress && window?.Cypress?.spec?.baseName !== 'Onboarding.test.feature';
    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');
    const isRedl = router?.query?.editmode === 'true' || router?.query?.editmode === 'yes';
    const nomCookie = getOnboardingCookieName(onboardingActiu);
    const estructuraCookieDefault = {
        lastShown: null,
        count: 0,
        totalCount: 0,
        variant: onboardingActiu?.defaultSegment || 'I'
    };
    const SHOW_INTERVAL_HOURS = onboardingActiu.horesCadaQuantEsMostra || 0;
    const MAX_TOTAL_ONBOARDING = onboardingActiu?.limitTotalQueEsMostra;

    // ✅ Mergear: base + variant
    const onboardingFinal = useMemo(() => {
        const variantOverrides = onboardingActiu?.variants?.[segment] || {};

        // Mergear només els camps modificables que tinguin valor
        return {
            ...baseProps,
            ...(variantOverrides?.titol && { titol: variantOverrides.titol }),
            ...(variantOverrides?.descripcio && { descripcio: variantOverrides.descripcio }),
            ...(variantOverrides?.subtitol && { subtitol: variantOverrides.subtitol }),
            ...(variantOverrides?.botons && { botons: variantOverrides.botons })
        };
    }, [segment, baseProps, onboardingActiu?.variants]);

    const saveOnStorage = (values) => {
        const expiresInDays = Math.ceil((onboardingActiu?.horesCadaQuantEsMostra || 24) / 24);
        setStorageValue(nomCookie, values, {
            path: '/3catinfo',
            domain: '.3cat.cat',
            expires: expiresInDays
        });
    };
    const initializeValues = (data) => {
        let response;
        if (__isUndefined(data)) {
            response = estructuraCookieDefault;
        } else {
            response = data;
            if (__isUndefined(response.count)) {
                response.count = 0;
            }
            if (__isUndefined(response.totalCount)) {
                response.totalCount = response.count;
            }
            if (__isUndefined(response.variant)) {
                response.variant = onboardingActiu?.defaultSegment || 'I';
            }
        }
        return response;
    };

    const isSameCalendarDay = (a, b) => {
        const firstDate = new Date(a);
        const secondDate = new Date(b);

        return (
            firstDate.getFullYear() === secondDate.getFullYear() &&
            firstDate.getMonth() === secondDate.getMonth() &&
            firstDate.getDate() === secondDate.getDate()
        );
    };

    const mostrarOnboarding = () => {
        // ✅ Comprovar si l'usuari qualifica per veure el modal
        const esLogat = isAuthenticated === true;

        if (esLogat && usuarisLogats === true) {
            return true; // ✅ Logat i permès per logats
        }

        if (!esLogat && usuarisNoLogats === true) {
            return true; // ✅ No logat i permès per no logats
        }

        return false; // ❌ No qualifica
    };

    const fetchDayCookieData = async () => {
        const data = await getStorageValue(nomCookie);
        let value = initializeValues(data);
        const nowMs = Date.now();

        // ✅ 1️⃣ PRIMER: Comprovar qualificació de l'usuari
        if (!mostrarOnboarding()) {
            console.log('⚠️ Usuari no qualifica per veure el modal');
            setCookieChecked(true);
            return;
        }

        // ✅ 2️⃣ SEGON: Comprovar si ha transcorregut temps suficient
        const intervalMs = SHOW_INTERVAL_HOURS * 60 * 60 * 1000;
        const tempsAssolit = value.lastShown === null || nowMs - value.lastShown >= intervalMs;
        const sHaMostratRecentment = value.lastShown !== null && nowMs - value.lastShown < intervalMs;

        if (value.lastShown !== null && (sHaMostratRecentment || isSameCalendarDay(value.lastShown, nowMs))) {
            console.log('⚠️ Ja s’ha mostrat recentment o avui, no es mostra de nou');
            setShowedToday(true);
            setCookieChecked(true);
            return;
        }

        if (tempsAssolit && value.lastShown !== null) {
            console.log(`✅ Han passat ${onboardingActiu?.horesCadaQuantEsMostra} hores, resetejant count`);
            value.count = 0;
            value.lastShown = nowMs;
        } else if (value.lastShown === null) {
            console.log('✅ Primera vegada, inicialitzant timestamp');
            value.lastShown = nowMs;
        }

        // ✅ 3️⃣ TERCER: Comprovar el límit total històric, si està configurat
        if (MAX_TOTAL_ONBOARDING > 0 && value.totalCount >= MAX_TOTAL_ONBOARDING) {
            console.log(`⚠️ Ja s'ha mostrat ${value.totalCount}/${MAX_TOTAL_ONBOARDING} vegades en total`);
            setCookieChecked(true);
            return;
        }

        // ✅ 4️⃣ QUART: Si useTarget és false, sempre usar defaultSegment
        if (!onboardingActiu?.useTarget) {
            const defaultSeg = onboardingActiu?.defaultSegment || 'I';
            setSegment(defaultSeg);
            value.variant = defaultSeg;
        } else {
            // ✅ Crida a Adobe Target només si qualifica i temps permès
            if (onboardingActiu?.targetMboxName) {
                try {
                    const variant = await fetchTargetVariant(
                        onboardingActiu.targetMboxName,
                        onboardingActiu.defaultSegment || 'I'
                    );
                    const finalSegment = isValidSegment(variant) ? variant : onboardingActiu?.defaultSegment || 'I';
                    setSegment(finalSegment);
                    value.variant = finalSegment;
                } catch (error) {
                    console.warn('Error cridant Adobe Target:', error);
                    value.variant = onboardingActiu?.defaultSegment || 'I';
                }
            }
        }

        // ✅ 5️⃣ CINQUÈ: Guardar cookie i marcar que es pot mostrar
        value.count++;
        value.totalCount++;
        value.lastShown = nowMs;
        saveOnStorage(value);
        // ✅ 6️⃣ SISÈ: Mostrar el modal revisant la variant i el useTarget
        if ((value.variant === onboardingActiu?.defaultSegment || value.variant === 'I') && onboardingActiu?.useTarget) {
            setShowOnboarding(false);
        } else {
            setShowOnboarding(true);
        }
        setCookieChecked(true);
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimeout(timeoutShowOnboarding.current);
        };
    }, []);

    // ✅ Sincronitzar la variant a la cookie quan canvia (NOMÉS si useTarget=true)
    useEffect(() => {
        if (!onboardingActiu?.useTarget) return; // No guardar variants si useTarget és false

        const updateVariantInCookie = async () => {
            const data = await getStorageValue(nomCookie);
            if (data) {
                data.variant = segment;
                const expiresInDays = Math.ceil((onboardingActiu?.horesCadaQuantEsMostra || 24) / 24);
                try {
                    setStorageValue(nomCookie, data, {
                        path: '/3catinfo',
                        domain: '.3cat.cat',
                        expires: expiresInDays
                    });
                } catch (err) {
                    console.warn('Error actualitzant variant a cookie:', err);
                }
            }
        };

        updateVariantInCookie();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segment, onboardingActiu?.useTarget]);

    useEffect(() => {
        // ✅ SEMPRE espera loginState fins que estigui en estat definitiu (AUTHENTICATED o NOT_AUTHENTICATED)
        // NO inicializing, NO undefined - necessitem saber de forma definitiva si està logat o no
        const isLoginStateResolved = loginState === loginStateTypes.AUTHENTICATED || loginState === loginStateTypes.GUEST;

        if (!initialFetchDone.current && onboardingActiu?.actiu && isLoginStateResolved) {
            fetchDayCookieData();
            initialFetchDone.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboardingActiu?.actiu, loginState, usuarisLogats, usuarisNoLogats, loginStateTypes]);

    useEffect(() => {
        if (!__isUndefined(refOnboarding.current) && showOnboarding === true && !isTest && !showedToday) {
            refOnboarding.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refOnboarding.current]);

    const tancaOnboarding = () => {
        removeModal();
        netejeUrlOnboarding();
        setShowOnboarding(false);
        setShowedToday(true);
    };

    const netejeUrlOnboarding = () => {
        let url = router.asPath?.split('#')[0];
        router.push(url, undefined, { scroll: false });
    };

    // ✅ Track click events a Target (NOMÉS si useTarget=true)
    // ⚠️ NO és async, es crida sense await per fer fire-and-forget
    const trackClickToTarget = (botoId) => {
        console.log('🔍 trackClickToTarget called:', {
            botoId,
            useTarget: onboardingActiu?.useTarget,
            hasTargetMbox: !!onboardingActiu?.targetMboxNameClick
        });

        if (!onboardingActiu?.useTarget) {
            console.log('⚠️ useTarget is false, skipping tracking');
            return;
        }

        if (!onboardingActiu?.targetMboxNameClick) {
            console.log('⚠️ targetMboxNameClick not configured, skipping tracking');
            return;
        }

        try {
            const clickMbox = `${onboardingActiu.targetMboxNameClick}`;
            console.log('✅ Sending to trackTargetClick:', clickMbox);
            trackTargetClick(clickMbox, botoId);
        } catch (err) {
            console.warn(`❌ Error tracking click [${botoId}]:`, err);
        }
    };

    // ✅ Actions amb tracking (NO espera, tracking és fire-and-forget)
    const actionsWithTracking = {
        signUp: (boto) => {
            console.log('📍 Action: signUp');
            trackClickToTarget('signUp');
            signUp();
        },
        signIn: (boto) => {
            console.log('📍 Action: signIn');
            trackClickToTarget('signIn');
            signIn();
        },
        close: (boto) => {
            console.log('📍 Action: close');
            trackClickToTarget('close');
            tancaOnboarding();
        },
        default: (boto) => {
            console.log('📍 Action: default');
            trackClickToTarget('default');
            tancaOnboarding();
            // Navigate to link if available
            if (boto?.type === 'link' && boto?.link) {
                setTimeout(() => {
                    window.location.href = boto.link;
                }, 100);
            }
        }
    };

    const mostrarOnboardingLegacy = (userLoginState) => {
        if (isAuthenticated === true) return usuarisLogats;
        if (isAuthenticated === false) return usuarisNoLogats;

        // Fallback while auth status is still being resolved.
        return userLoginState === loginStateTypes.GUEST ? usuarisNoLogats : usuarisLogats;
    };

    // ✅ Tancar el modal si l'usuari deixa de qualificar
    useEffect(() => {
        if (!mostrarOnboardingLegacy(loginState)) {
            setShowOnboarding(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, isAuthenticated, usuarisLogats, usuarisNoLogats]);

    const actions = {
        signUp: () => signUp(),
        signIn: () => signIn(),
        close: () => tancaOnboarding()
    };

    // ✅ Suportar estils de `base` o `onboardingActiu`
    const estilosString = onboardingFinal.estils || onboardingActiu.estils || '';
    const arrayEstils = estilosString?.split(' ') || [];
    const stylesOnboarding = clsx({
        [styles.onBoardingWrapper]: true,
        ...arrayEstils.reduce((acc, currentStyle) => {
            acc[styles[currentStyle]] = !__isEmpty(currentStyle);
            return acc;
        }, {})
    });

    const componentId = useId().replace(/:/g, '-');
    const containerId = `custom-container-onboarding-${componentId}`;
    const cssProcessat = onboardingActiu.css
        ? `${onboardingActiu.css.replace(/(^|[^}]+)\s*\{/g, (match) => `#${containerId} ${match.trim()}`)}`
        : '';

    const processStyleValue = (value) => {
        if (!value) return undefined;
        // Si comença amb --, és referència a variable CSS, posar var()
        if (value.toString().startsWith('--')) {
            return `var(${value})`;
        }
        // Sinó, retornar el valor directe
        return value;
    };

    const contentOnBoarding = () => {
        const stylesVarsSubtitol = {
            ...(onboardingFinal.subtitol?.color && { '--text-subtitol-color': onboardingFinal.subtitol.color }),
            ...(onboardingFinal.subtitol?.fontSize && {
                '--text-subtitol-fontSize': processStyleValue(onboardingFinal.subtitol.fontSize)
            }),
            ...(onboardingFinal.subtitol?.lineHeight && {
                '--text-subtitol-lineHeight': processStyleValue(onboardingFinal.subtitol.lineHeight)
            })
        };

        const stylesVarsTitol = {
            ...(onboardingFinal.titol?.color && { '--text-titol-color': onboardingFinal.titol.color }),
            ...(onboardingFinal.titol?.fontSize && {
                '--text-titol-fontSize': processStyleValue(onboardingFinal.titol.fontSize)
            }),
            ...(onboardingFinal.titol?.lineHeight && {
                '--text-titol-lineHeight': processStyleValue(onboardingFinal.titol.lineHeight)
            })
        };

        return (
            <div
                data-testid={`onboarding-custom-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div className={styles.mask + ' ' + styles.onboarding} onClick={() => tancaOnboarding()} ref={refOnboarding}>
                    <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.img}>
                            <div className={styles.imgTop}>
                                <Imatge
                                    src={onboardingFinal.imatge?.src}
                                    layout={onboardingFinal.imatge?.layout}
                                    objectFit={onboardingFinal.imatge?.objectFit}
                                    alt={onboardingFinal.imatge?.alt}
                                    //className={styles.imatgeModal}
                                />
                            </div>
                        </div>
                        <div className={styles.text}>
                            <div className={styles.imgBottom}>
                                <Imatge
                                    src={onboardingFinal.imatgeMobile?.src}
                                    layout={onboardingFinal.imatgeMobile?.layout}
                                    objectFit={onboardingFinal.imatgeMobile?.objectFit}
                                    alt={onboardingFinal.imatgeMobile?.alt}
                                />
                            </div>
                            <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                <Icona
                                    icona='tanca'
                                    className={styles.ico_tanca}
                                    alt='Tanca'
                                    ariaHidden={true}
                                    isBlanc={onboardingActiu?.icona_tanca?.color === 'blanc' || false}
                                />
                                <span className='sr-only'>Tanca finestra modal</span>
                            </button>

                            <div className={styles.contingutMobil}>
                                <div className={styles.wrapperLogo}>
                                    <Imatge
                                        src={onboardingFinal.logo?.src}
                                        width={onboardingFinal.logo?.width}
                                        height={onboardingFinal.logo?.height}
                                        alt={onboardingFinal.logo?.alt}
                                    />
                                </div>
                                <Text
                                    tag='h1'
                                    className={styles.title}
                                    text={onboardingFinal.titol.text}
                                    style={stylesVarsTitol}
                                />
                                <Text
                                    tag='div'
                                    className={styles.subtitle}
                                    text={onboardingFinal.subtitol.text}
                                    style={stylesVarsSubtitol}
                                />

                                <div className={styles.btns}>
                                    {onboardingFinal.botons?.map((boto, index) => {
                                        const styleVarsBoto = {
                                            '--btn-position': boto?.position,
                                            '--btn-text-color': boto?.textColor,
                                            '--btn-background-color': boto?.backgroundColor,
                                            '--btn-border-color': boto?.borderColor,
                                            '--btn-text-color-hover': boto?.textColorHover,
                                            '--btn-background-color-hover': boto?.backgroundColorHover,
                                            '--btn-border-color-hover': boto?.borderColorHover,
                                            '--btn-text-fontSize': boto?.fontSize && `var(${boto?.fontSize})`,
                                            '--btn-text-lineHeight': boto?.lineHeight && `var(${boto?.lineHeight})`,
                                            '--btn-text-fontWeight': boto?.fontWeight
                                        };

                                        return (
                                            <div key={`boto-wrapper-${index}`} style={styleVarsBoto}>
                                                <Boto
                                                    key={`boto-onboarding-${index}`}
                                                    tag={boto.tag ?? (boto.action ? 'button' : 'a')}
                                                    link={boto.link}
                                                    route={boto.route}
                                                    novaFinestra={boto.novaFinestra}
                                                    text={boto.text}
                                                    estil={boto.botoEstil}
                                                    className={clsx({
                                                        [styles.boto]: true,
                                                        ...(boto.estils?.split(' ') || []).reduce((acc, classe) => {
                                                            acc[styles[classe] || classe] = !__isEmpty(classe);
                                                            return acc;
                                                        }, {})
                                                    })}
                                                    onClick={(e) => {
                                                        console.log('Boto clicked:', index, {
                                                            type: boto.type,
                                                            action: boto.action
                                                        });

                                                        // Si és un link sense action, deixar navegació normal
                                                        if (boto.type === 'link' && !boto.action) return;

                                                        // Si té action, executar-la
                                                        if (boto.action) {
                                                            const action = actionsWithTracking[boto.action];
                                                            if (action) {
                                                                action(boto);
                                                            }
                                                        }
                                                    }}
                                                    dataTestId={boto.dataTestId}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const contentOnBoardingComponent = () => {
        // ✅ Helper per convertir boto singular a array
        const getBotons = (botoField) => {
            if (!botoField) return [];
            if (Array.isArray(botoField)) return botoField;
            // Si és objecte singular (boto), retornar com array
            if (typeof botoField === 'object') return [botoField];
            return [];
        };

        return (
            <div
                data-testid={`onboarding-base-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div className={styles.mask + ' ' + styles.onboarding} onClick={() => tancaOnboarding()} ref={refOnboarding}>
                    <div
                        className={styles.wrapper}
                        onClick={(e) => e.stopPropagation()}
                        style={{ '--wrapper-width': onboardingActiu.width }}
                    >
                        <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                            <Icona
                                icona='tanca'
                                isBlanc={onboardingActiu?.icona_tanca?.color === 'blanc' || false}
                                className={styles.ico_tanca_blanc}
                                alt='Tanca'
                                ariaHidden={true}
                            />
                            <span className='sr-only'>Tanca finestra modal</span>
                        </button>
                        <ComponentOnboarding
                            {...onboardingFinal}
                            image={
                                onboardingFinal.imatge
                                    ? {
                                          ...onboardingFinal.imatge,
                                          url: onboardingFinal.imatge.src,
                                          urlMobile: onboardingFinal.imatgeMobile?.src
                                      }
                                    : undefined
                            }
                            //imatgeMobile={onboardingFinal.imatgeMobile ? { ...onboardingFinal.imatgeMobile, url: onboardingFinal.imatgeMobile.src } : undefined}
                            boto={
                                getBotons(onboardingFinal.botons)?.[0]
                                    ? {
                                          ...getBotons(onboardingFinal.botons)?.[0],
                                          url: getBotons(onboardingFinal.botons)?.[0]?.link
                                      }
                                    : undefined
                            }
                            onClose={() => tancaOnboarding()}
                            onTargetClick={() => {
                                if (onboardingActiu?.targetMboxNameClick) {
                                    trackTargetClick(onboardingActiu.targetMboxNameClick, 'modal-button');
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!onboardingActiu) return;

        if (showOnboarding && !isTest && !isEmbedOrPopup && !isModulAvisActive) {
            setModal(
                onboardingActiu.tipus === 'component' && !onboardingActiu.useTarget && onboardingActiu.component
                    ? contentOnBoardingComponent()
                    : contentOnBoarding()
            );
            return;
        }

        removeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, isTest, isEmbedOrPopup, onboardingActiu, ComponentOnboarding, isModulAvisActive, cookieChecked]);

    return null;
}
